import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Loader2, AlertTriangle, TrendingUp, Users, Clock } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";

type FailureStats = {
  step: string;
  total_failures: number;
  unique_users_affected: number;
  days_with_failures: number;
  error_messages: string[];
  last_failure_at: string;
};

type RecentAttempt = {
  id: string;
  user_id: string;
  board_type: string;
  step: string;
  status: string;
  error_message: string | null;
  created_at: string;
};

const stepLabels: Record<string, string> = {
  workspace_fetch: "Workspace Fetch",
  board_create: "Board Creation",
  personas_create: "Personas Creation",
  project_create: "Project Creation",
  session_create: "Session Creation",
};

export const BoardCreationMonitor = () => {
  const [timeRange, setTimeRange] = useState<"24h" | "7d" | "30d">("7d");

  const {
    data: failureStats = [],
    isLoading: isLoadingStats,
    refetch: refetchStats,
  } = useQuery<FailureStats[], Error>({
    queryKey: ['boardCreationFailureStats'],
    queryFn: async () => {
      const { data, error } = await supabase.from("board_creation_failure_stats").select("*");
      if (error) {
        toast.error("Failed to load failure statistics");
        throw error;
      }
      return data as FailureStats[];
    },
  });

  const {
    data: recentAttempts = [],
    isLoading: isLoadingAttempts,
    refetch: refetchAttempts,
  } = useQuery<RecentAttempt[], Error>({
    queryKey: ['boardCreationRecentAttempts', timeRange],
    queryFn: async () => {
      const hoursAgo = timeRange === "24h" ? 24 : timeRange === "7d" ? 168 : 720;
      const cutoffTime = new Date();
      cutoffTime.setHours(cutoffTime.getHours() - hoursAgo);

      const { data, error } = await supabase
        .from("board_creation_attempts")
        .select("*")
        .gte("created_at", cutoffTime.toISOString())
        .order("created_at", { ascending: false })
        .limit(50);
      
      if (error) {
        toast.error("Failed to load recent attempts");
        throw error;
      }
      return data as RecentAttempt[];
    },
  });

  const loading = isLoadingStats || isLoadingAttempts;

  const fetchData = () => {
    refetchStats();
    refetchAttempts();
  };

  const getSuccessRate = () => {
    const total = recentAttempts.length;
    if (total === 0) return 0;
    const successful = recentAttempts.filter((a) => a.status === "success").length;
    return Math.round((successful / total) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const successRate = getSuccessRate();
  const totalFailures = failureStats.reduce((sum, stat) => sum + stat.total_failures, 0);
  const uniqueUsersAffected = Math.max(
    ...failureStats.map((stat) => stat.unique_users_affected),
    0
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Board Creation Monitor</h2>
          <p className="text-muted-foreground">
            Track failures and identify problematic steps
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={timeRange === "24h" ? "default" : "outline"}
            size="sm"
            onClick={() => setTimeRange("24h")}
          >
            24h
          </Button>
          <Button
            variant={timeRange === "7d" ? "default" : "outline"}
            size="sm"
            onClick={() => setTimeRange("7d")}
          >
            7d
          </Button>
          <Button
            variant={timeRange === "30d" ? "default" : "outline"}
            size="sm"
            onClick={() => setTimeRange("30d")}
          >
            30d
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{successRate}%</div>
            <p className="text-xs text-muted-foreground">
              {recentAttempts.filter((a) => a.status === "success").length} of{" "}
              {recentAttempts.length} attempts
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Failures</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalFailures}</div>
            <p className="text-xs text-muted-foreground">All-time failures</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Users Affected</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{uniqueUsersAffected}</div>
            <p className="text-xs text-muted-foreground">Unique users impacted</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Recent Activity</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{recentAttempts.length}</div>
            <p className="text-xs text-muted-foreground">Attempts in {timeRange}</p>
          </CardContent>
        </Card>
      </div>

      {/* Failure by Step */}
      <Card>
        <CardHeader>
          <CardTitle>Failures by Step</CardTitle>
          <CardDescription>
            Identify which steps are causing the most problems
          </CardDescription>
        </CardHeader>
        <CardContent>
          {failureStats.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No failure data available
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Step</TableHead>
                  <TableHead>Total Failures</TableHead>
                  <TableHead>Users Affected</TableHead>
                  <TableHead>Last Failure</TableHead>
                  <TableHead>Common Errors</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {failureStats.map((stat) => (
                  <TableRow key={stat.step}>
                    <TableCell className="font-medium">
                      {stepLabels[stat.step] || stat.step}
                    </TableCell>
                    <TableCell>
                      <Badge variant="destructive">{stat.total_failures}</Badge>
                    </TableCell>
                    <TableCell>{stat.unique_users_affected}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(stat.last_failure_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="max-w-md">
                      <div className="space-y-1">
                        {stat.error_messages.slice(0, 3).map((msg, idx) => (
                          <p key={idx} className="text-xs text-muted-foreground truncate">
                            {msg}
                          </p>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Recent Attempts */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Attempts</CardTitle>
          <CardDescription>Latest board creation attempts</CardDescription>
        </CardHeader>
        <CardContent>
          {recentAttempts.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No recent attempts
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Board Type</TableHead>
                  <TableHead>Step</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentAttempts.map((attempt) => (
                  <TableRow key={attempt.id}>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(attempt.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{attempt.board_type}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {stepLabels[attempt.step] || attempt.step}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={attempt.status === "success" ? "default" : "destructive"}
                      >
                        {attempt.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-md">
                      {attempt.error_message && (
                        <p className="text-xs text-muted-foreground truncate">
                          {attempt.error_message}
                        </p>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
